import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import useMessageMutation from "@/hooks/useMessageMutation";
import type { PostCreateMessageBodySchema } from "@/schemas";
import type { MessageDiscussion } from "@/types";
import { flip, offset, shift, useFloating } from "@floating-ui/react";
import ErrorIcon from "@mui/icons-material/Error";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import StrikethroughSOutlinedIcon from "@mui/icons-material/StrikethroughSOutlined";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate, useParams } from "@tanstack/react-router";
import isEmpty from "lodash/isEmpty";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface ContextMenuMessageProps {
	isReadonly: boolean;
	channelName: string;
	moduleType: "chats" | "cases";
	message: MessageDiscussion;
	profileId: number;
	position: Record<"x" | "y", number> | null;
	onClose: () => void;
}

const ContextMenuMessage = (props: ContextMenuMessageProps) => {
	const { t } = useTranslation("common");
	const {
		isReadonly,
		channelName,
		moduleType,
		message,
		profileId,
		position,
		onClose,
	} = props;
	const messageableId = useParams({
		strict: false,
		select: (s) => s.id,
	});
	const [, onCopyClipboard] = useCopyToClipboard();
	const navigate = useNavigate();
	const { deleteMessage, update } = useMessageMutation();
	const { refs, strategy, x, y } = useFloating({
		placement: "top-start",
		middleware: [
			offset(10),
			flip({
				fallbackAxisSideDirection: "start",
			}),
			shift(),
		],
	});

	const { setValue } = useFormContext<PostCreateMessageBodySchema>();

	if (!position || !message || !channelName) return null;

	const isMe = profileId === message.user?.id;
	const canCopy = !isEmpty(message.text);
	const canAlert = !isReadonly && message.document_count <= 0;
	const showDelete = !isReadonly && isMe && moduleType !== "cases";
	const showStrike = !isReadonly && !message.strike && isMe;

	const onClickStrike = (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		return update.mutate(
			{
				id: message.id_db,
				channelName,
				strike: !message.strike,
				messageable_id: messageableId,
			},
			{
				onSuccess: () => {
					onClose();
				},
			},
		);
	};

	const onClickDelete = (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		return deleteMessage.mutate(
			{
				id: message.id_db,
				channelName: message.channel_name,
			},
			{
				onSuccess: () => {
					onClose();
				},
			},
		);
	};

	const onClickCopy = (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		const text = message.text;
		if (!text) return;
		return onCopyClipboard(text)
			.then(() => {
				toast.success(t("common:copied"));
			})
			.catch(() => {
				toast.error(t("common:copyFailed"));
			});
	};

	const onClickReply = async (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		await Promise.all([
			setValue("parent_id", message.id_db),
			setValue("message", message),
		]);
		return onClose();
	};

	const onClickAlert = async (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		return navigate({
			to: "/dashboard/inbox/reminders/create",
			search: {
				message: message.id_db,
			},
		}).then(() => {
			onClose();
		});
	};

	return (
		<Menu
			ref={refs.setFloating}
			open={position !== null}
			onClose={onClose}
			anchorReference="anchorPosition"
			anchorPosition={
				position !== null ? { top: position.y, left: position.x } : undefined
			}
			style={{
				position: strategy,
				top: y ?? 0,
				left: x ?? 0,
			}}
			classes={{
				paper: "w-60 !rounded-lg",
			}}
		>
			<MenuItem
				onClick={onClickReply}
				className={isReadonly ? "!hidden" : undefined}
			>
				<ListItemIcon>
					<ReplyOutlinedIcon fontSize="small" className="text-zinc-950" />
				</ListItemIcon>
				<ListItemText>{t("common:reply")}</ListItemText>
			</MenuItem>
			<Divider className={!canCopy || isReadonly ? "!hidden" : undefined} />
			<MenuItem
				onClick={onClickCopy}
				className={!canCopy ? "!hidden" : undefined}
			>
				<ListItemIcon>
					<FileCopyOutlinedIcon fontSize="small" className="text-zinc-950" />
				</ListItemIcon>
				<ListItemText>{t("common:copy")}</ListItemText>
			</MenuItem>
			<Divider className={!showStrike ? "!hidden" : undefined} />
			<MenuItem
				className={!showStrike ? "!hidden" : undefined}
				onClick={onClickStrike}
			>
				<ListItemIcon>
					<StrikethroughSOutlinedIcon
						fontSize="small"
						className="text-zinc-950"
					/>
				</ListItemIcon>
				<ListItemText>{t("common:strikeover")}</ListItemText>
			</MenuItem>
			<Divider className={!showDelete ? "!hidden" : undefined} />
			<MenuItem
				className={!showDelete ? "!hidden" : undefined}
				onClick={onClickDelete}
			>
				<ListItemIcon>
					<RemoveCircleIcon fontSize="small" className="text-zinc-950" />
				</ListItemIcon>
				<ListItemText>{t("common:delete")}</ListItemText>
			</MenuItem>
			<Divider className={!canAlert ? "!hidden" : undefined} />
			<MenuItem
				className={!canAlert ? "!hidden" : undefined}
				onClick={onClickAlert}
			>
				<ListItemIcon>
					<ErrorIcon fontSize="small" className="text-zinc-950" />
				</ListItemIcon>
				<ListItemText>{t("common:alertMe")}</ListItemText>
			</MenuItem>
		</Menu>
	);
};

export default ContextMenuMessage;
